import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useGoogleLogin } from '@react-oauth/google';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    TextField,
    Button,
    CircularProgress,
    Typography,
    Paper,
    Collapse,
    Box,
    Select,
    MenuItem,
    Alert
} from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import GoogleLogo from '../assets/g-logo.png'; // Assuming the logo is in src/assets

const BACKEND = 'https://lobster-app-sd3s8.ondigitalocean.app';

const GSCChatInterface = ({ token: initialToken, disabled, handleError: parentErrorHandler }) => {
    // FIX 1: Removed unused `setToken` as it's not updated within this component.
    const [token] = useState(initialToken);
    const [selectedProperty, setSelectedProperty] = useState('');
    const [properties, setProperties] = useState([]);
    const [question, setQuestion] = useState('');
    const [chatHistory, setChatHistory] = useState([]);
    const [loading, setLoading] = useState(true); // Start loading immediately to fetch properties
    const [jobId, setJobId] = useState(null);
    const [expanded, setExpanded] = useState({});
    
    // **NEW**: State to track if GSC authorization is needed.
    const [gscAuthRequired, setGscAuthRequired] = useState(false);

    const localHandleError = useCallback((err, retryCallback = null) => {
        const errorData = err.response?.data?.error;
        const statusCode = err.response?.status;

        // **MODIFIED**: Check for the specific GSC_AUTH_REQUIRED error
        if (statusCode === 403 && errorData?.code === 'GSC_AUTH_REQUIRED') {
            setGscAuthRequired(true);
            setLoading(false);
            return; // Stop further processing
        }
        
        // Handle expired token (401)
        if (statusCode === 401) {
            console.log("Caught 401, session likely expired.");
            parentErrorHandler({ message: 'Your session has expired. Please log in again.' }, () => {
                window.location.href = '/'; // Force re-login
            });
            return;
        }

        // Handle other errors
        if (parentErrorHandler) {
            parentErrorHandler({ message: errorData?.message || err.message || 'An unexpected error occurred.' }, retryCallback);
        }
    // FIX 3: Removed unnecessary 'token' dependency.
    }, [parentErrorHandler]);

    // --- Backend API Call Handlers ---
    const fetchGSCProperties = useCallback(async (apiToken) => {
        try {
            setLoading(true);
            setGscAuthRequired(false);
            const response = await axios.get(`${BACKEND}/api/gsc/sites`, {
                headers: { Authorization: `Bearer ${apiToken}` }
            });
            const siteData = response.data.sites || [];
            setProperties(siteData);
            if (siteData.length > 0) {
                setSelectedProperty(siteData[0].siteUrl);
            } else {
                // If no properties are found even after auth, show a message.
                setProperties([]);
            }
        } catch (err) {
            localHandleError(err, () => fetchGSCProperties(apiToken));
        } finally {
            setLoading(false);
        }
    // FIX 2: Added missing 'localHandleError' dependency.
    }, [localHandleError]);

    // --- Authentication and Error Handling ---
    const gscLogin = useGoogleLogin({
        flow: 'auth-code', // This is crucial to get the code for the backend
        scope: "https://www.googleapis.com/auth/webmasters.readonly",
        onSuccess: async (codeResponse) => {
            try {
                setLoading(true);
                // Send the authorization code to your backend
                await axios.post(`${BACKEND}/auth/gsc`, 
                    { credential: codeResponse.code }, // Sending the code
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                // After successful authorization, refetch the GSC properties
                setGscAuthRequired(false);
                fetchGSCProperties(token);
            } catch (err) {
                parentErrorHandler({ message: err.response?.data?.error?.message || 'Failed to authorize GSC.' });
                setLoading(false);
            }
        },
        onError: () => {
            parentErrorHandler({ message: 'Google authorization failed. Please try again.' });
        }
    });

    useEffect(() => {
        if (token) {
            fetchGSCProperties(token);
        }
    }, [token, fetchGSCProperties]);

    // Polling for job status (unchanged logic, but depends on corrected error handling)
    useEffect(() => {
        if (!jobId) return;

        const interval = setInterval(async () => {
            try {
                const response = await axios.get(`${BACKEND}/api/job/${jobId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                const { status, result, error } = response.data;

                if (status === 'completed') {
                    const newEntry = { question, answer: result.answer };
                    setChatHistory(prev => [...prev, newEntry]);
                    setExpanded(prev => ({ ...prev, [chatHistory.length]: true }));
                    setQuestion('');
                    setLoading(false);
                    setJobId(null);
                    clearInterval(interval);
                } else if (status === 'failed') {
                    parentErrorHandler({ message: error || 'Failed to process your question.' });
                    setLoading(false);
                    setJobId(null);
                    clearInterval(interval);
                }
            } catch (err) {
                localHandleError(err);
                setLoading(false);
                setJobId(null);
                clearInterval(interval);
            }
        }, 5000);

        return () => clearInterval(interval);
    }, [jobId, token, question, chatHistory.length, localHandleError, parentErrorHandler]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!question.trim() || !selectedProperty || disabled) return;
        setLoading(true);

        try {
            const response = await axios.post(
                `${BACKEND}/api/gsc/chat`,
                {
                    propertyUrl: selectedProperty,
                    question,
                    startDate: '2025-01-01',
                    endDate: '2025-06-13'
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setJobId(response.data.jobId);
        } catch (err) {
            localHandleError(err, () => handleSubmit(e));
            setLoading(false);
        }
    };
    
    // --- JSX Rendering ---

    if (loading && !jobId) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading GSC Data...</Typography>
            </Box>
        );
    }

    // **NEW**: Render authorization prompt if required
    if (gscAuthRequired) {
        return (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h3" gutterBottom>Connect to Google Search Console</Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    To use this feature, you need to grant access to your Google Search Console data.
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => gscLogin()}
                    startIcon={<img src={GoogleLogo} alt="Google logo" style={{ width: 20, height: 20 }} />}
                    sx={{ py: 1.5, px: 4, fontSize: '1.1rem' }}
                >
                    Connect with Google
                </Button>
            </Paper>
        );
    }
    
    // Render the main chat interface
    return (
        <div className="chat-container">
            <Typography variant="h2">Chat with Google Search Console Data</Typography>
            <Box sx={{ mb: 2 }}>
                <Typography variant="body1" sx={{ mb: 1 }}>Select GSC Property:</Typography>
                {properties.length === 0 ? (
                     <Alert severity="warning">No GSC properties found. Please ensure you have granted access to the correct Google Account.</Alert>
                ) : (
                    <Select
                        value={selectedProperty}
                        onChange={(e) => setSelectedProperty(e.target.value)}
                        displayEmpty
                        fullWidth
                        disabled={disabled}
                        sx={{ background: '#f7fafc', color: '#2d3748' }}
                    >
                        {properties.map((prop) => (
                            <MenuItem key={prop.siteUrl} value={prop.siteUrl}>
                                {prop.siteUrl}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </Box>

            <div className="chat-history">
                {chatHistory.map((entry, index) => (
                    <div key={index} className="chat-entry">
                        <Box
                            display="flex"
                            alignItems="center"
                            sx={{ cursor: 'pointer' }}
                            onClick={() => setExpanded(prev => ({...prev, [index]: !prev[index]}))}
                        >
                            <Typography sx={{ flexGrow: 1 }}>
                                <strong>Q:</strong> {entry.question}
                            </Typography>
                            {expanded[index] ? <ExpandLess /> : <ExpandMore />}
                        </Box>
                        <Collapse in={expanded[index]}>
                            <Paper elevation={1} className="chat-answer" sx={{ mt: 1, p: 2 }}>
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                    {entry.answer}
                                </ReactMarkdown>
                            </Paper>
                        </Collapse>
                    </div>
                ))}
            </div>

            {(loading && jobId) && (
                <div className="loading-indicator">
                    <CircularProgress />
                    <Typography>Processing your question...</Typography>
                </div>
            )}

            <form onSubmit={handleSubmit} className="chat-form">
                <TextField
                    variant="outlined"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    placeholder="Ask a question about your GSC data..."
                    disabled={disabled || loading || !selectedProperty}
                    fullWidth
                    sx={{ background: '#f7fafc', color: '#2d3748' }}
                />
                <Button type="submit" variant="contained" color="primary" disabled={disabled || loading || !selectedProperty}>
                    {loading ? 'Asking...' : 'Ask'}
                </Button>
            </form>
        </div>
    );
};

export default GSCChatInterface;
